import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button';
import { useCallback, useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { organizationFields } from '../../../constants';
import type { OrganizationFormData } from '../../../types';
import { useOrganizationContext } from '../../../utils/context/OrganizationContext';
import { DspRestrictionTooltip } from '../../DspRestrictionTooltip';
import { DspRestrictionFormInputs } from './DspRestrictionFormInputs';

export const DspRestrictionSection = ({
  initialFormValues,
  isEdit,
}: {
  initialFormValues: OrganizationFormData;
  isEdit?: boolean;
}) => {
  const { t } = useTranslation();

  const { control, watch } = useFormContext<OrganizationFormData>();
  const observableDspRestriction = watch(organizationFields.DSP_RESTRICTION);
  const { fields, append, remove } = useFieldArray({ name: organizationFields.DSP_RESTRICTION });

  const { useDspsFetch } = useOrganizationContext();
  const { mapById: dspOptions, loading: dspLoading } = useDspsFetch();

  useEffect(() => {
    if (isEdit && (!initialFormValues.dsp_restriction || initialFormValues.dsp_restriction.length === 0)) {
      return;
    }
    if (fields.length === 0 && (!observableDspRestriction || observableDspRestriction.length === 0)) {
      append({ dsp_id: null, seat_ids: [] });
    }
  }, [fields, append, observableDspRestriction, isEdit, initialFormValues.dsp_restriction]);

  const addAnotherDsp = useCallback(() => {
    append({ dsp_id: null, seat_ids: [] });
  }, [append]);

  const tooltipInfoTextLines = [
    t(
      'Buyer organizations have a restricted list of DSPs and Seats available. This does not apply to other organizations.'
    ),
    t(
      'Add any DSPs and seats needed by the organizations. Once saved, available DSPs and seats cannot be removed due to potential active use in live deals.'
    ),
  ];

  return (
    <>
      <Grid
        size={12}
        sx={{
          mb: 0.5,
          mt: 2,
        }}
      >
        <Typography variant="body1Bold" data-test="dsp-seats-label" sx={{ textTransform: 'uppercase' }}>
          {t('Available DSP and Seats')}
          <DspRestrictionTooltip tooltipTextLines={tooltipInfoTextLines} />
        </Typography>
      </Grid>
      <Grid
        container
        wrap="nowrap"
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <Grid size={{ xs: 12 }} sx={{ minWidth: '100%' }}>
          {fields.map((field, index) => {
            const isInitial =
              Array.isArray(initialFormValues.dsp_restriction) && index < initialFormValues.dsp_restriction.length;
            const initialDspId = isInitial ? initialFormValues.dsp_restriction?.[index].dsp_id : null;
            const initialSeatIds = isInitial ? initialFormValues.dsp_restriction?.[index].seat_ids : [];

            return (
              <DspRestrictionFormInputs
                key={field.id}
                index={index}
                fieldId={field.id}
                control={control}
                dspOptions={dspOptions}
                dspLoading={dspLoading}
                remove={remove}
                canRemove={true}
                isInitial={isInitial}
                initialDspId={initialDspId}
                initialSeatIds={initialSeatIds}
                initialDspRestrictionMode={initialFormValues.dsp_restriction_mode}
              />
            );
          })}
          <Box>
            <Button onClick={addAnotherDsp} data-test="add-dsp" color="primary" type="button" variant="text">
              {t('Add another DSP')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
